import { IAccountTabType, ILoggedIn, RootState } from '@checkout/state';
import * as cx from 'classnames';
import * as React from 'react';
import { Fragment } from 'react';
import { InjectedIntlProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { AccountDetailsForm, ChangePasswordForm, ChecksList, MembershipForms, OrderHistory } from '..';
import { accountChangeTab, logOutRequest, ordersHistoryRequest, resetErrors, resetNotifications } from '../../../reducers/checkout';
import { Button } from '../../presentational';
import { CheckoutContextConfig } from './../../../apps/CheckoutApp';
import { ICheckoutContextConfig } from './../../../config/contextCheckoutConfig.d';
import messages from './../../../i18n/base-en';
import * as Styles from './MembershipAccount.scss';

interface ITab {
  title: string;
  type: IAccountTabType;
  component: React.ComponentClass;
  onSelect?: () => void;
  checkoutContextConfig?: ICheckoutContextConfig;
}

interface IOwnProps {}

interface IConnectedProps {
  loggedIn: ILoggedIn | null;
  selectedTab: IAccountTabType;
}

interface IConnectetDispatchProps {
  logOutRequest: typeof logOutRequest;
  accountChangeTab: typeof accountChangeTab;
  resetErrors: typeof resetErrors;
  resetNotifications: typeof resetNotifications;
  ordersHistoryRequest: typeof ordersHistoryRequest;
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedIntlProps;

class MembershipAccount extends React.Component<Props, {}> {

  getTabs = (checkoutContextConfig: ICheckoutContextConfig): ITab[] => {
    const { formatMessage } = this.props.intl;

    return [
      {
        component: AccountDetailsForm,
        title: formatMessage(messages.membership_tabs_personal),
        checkoutContextConfig,
        type: 'personal'
      },
      {
        component: OrderHistory,
        title: formatMessage(messages.membership_tabs_orders),
        checkoutContextConfig,
        type: 'orders',
        onSelect: () => this.props.ordersHistoryRequest()
      },
      {
        component: ChecksList,
        title: formatMessage(messages.membership_tabs_checks),
        checkoutContextConfig,
        type: 'checks'
      },
      {
        component: ChangePasswordForm,
        title: formatMessage(messages.membership_tabs_password),
        checkoutContextConfig,
        type: 'password'
      },
    ];
  }

  logout = () => {
    this.props.logOutRequest();
  }

  selectTab = (tab: ITab) => {
    return (event: React.MouseEvent<HTMLElement>) => {
      event.preventDefault();
      this.props.accountChangeTab({
        type: tab.type
      });
      this.props.resetErrors();
      this.props.resetNotifications();
      if (tab.onSelect) {
        tab.onSelect();
      }
    };
  }

  filterTabs = (checkoutContextConfig: ICheckoutContextConfig) => {
    const enabledTabs = checkoutContextConfig.modules.membership.enabledTabs;
    return (tab: ITab) => enabledTabs.indexOf(tab.type) > -1;
  }

  render() {
    const { loggedIn, selectedTab, intl } = this.props;
    const { formatMessage } = intl;

    return (
      <CheckoutContextConfig.Consumer>
        {(checkoutContextConfig => (
          <Fragment>
            {loggedIn && (
              <Fragment>
                <div className={Styles.header}>
                  <h1>{formatMessage(messages.membership_greeting)}, {loggedIn.firstName}</h1>
                  <p>{formatMessage(messages.membership_account_subtitle)}</p>
                  <Button
                    onClick={this.logout}
                    type="button"
                    variant="clear"
                    align="center"
                    size="medium"
                    label={formatMessage(messages.membership_logout)}
                  />
                </div>
                <div>
                  <div className={Styles.tabs}>
                    {this.getTabs(checkoutContextConfig).filter(this.filterTabs(checkoutContextConfig)).map(tab => {
                      const tabClassNames = cx({
                        [Styles.tab]: true,
                        [Styles.selectedTab]: selectedTab === tab.type,
                      });
                      return (
                        <a key={`tab-${tab.type}`} onClick={this.selectTab(tab)} className={tabClassNames}>
                          <span>{tab.title}</span>
                        </a>
                      );
                    })}
                  </div>
                  {this.getTabs(checkoutContextConfig).filter(this.filterTabs(checkoutContextConfig)).filter(tab => selectedTab === tab.type).map(tab => (
                    React.createElement(tab.component, { ...tab, key: `tabContent-${tab.type}` })
                  ))}
                </div>
              </Fragment>
            )}
            {loggedIn === null && (
              <MembershipForms />
            )}
          </Fragment>
        ))}
      </CheckoutContextConfig.Consumer>
    );
  }
}

export default compose<React.ComponentClass>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      loggedIn: state.checkout.loggedIn,
      selectedTab: state.checkout.ui.membership.selectedTab,
    }),
    {
      logOutRequest,
      accountChangeTab,
      resetErrors,
      resetNotifications,
      ordersHistoryRequest,
    }
  ),
)(MembershipAccount);
